import { useSitecoreContext } from 'hooks/useSitecoreContext';
import { ComponentProps } from 'lib/component-props';
import { useEffect, useMemo } from 'react';
import { useLazyArticlesSearchQuery } from '@/store/articles/services/articleSearch';
import { useSelector } from 'react-redux';
import { getArticlesData } from '@/store/articles';
import ArticleCardsComponent from 'components/Generic/ArticleCardsComponent';
import { useArticlePagination } from 'hooks/useArticlePagination';
import { ArticleFields } from 'components/Tiles/ArticleCard';

const ArticleCards = ({ rendering }: ComponentProps): JSX.Element => {
  const { sitecoreContext } = useSitecoreContext();
  const [fetchArticles, { data, isFetching }] = useLazyArticlesSearchQuery();
  const { appliedFilters, contentType } = useSelector(getArticlesData);
  const { currentPage, itemsPerPage, handlePageNumberChange, handlePageSizeChange } =
    useArticlePagination(appliedFilters);

  useEffect(() => {
    if (itemsPerPage && contentType !== undefined) {
      fetchArticles({
        filters: appliedFilters,
        search: {
          sites: [sitecoreContext.site?.name],
          language: sitecoreContext.language,
          entityTypes: ['article'],
          contentTypes: (contentType && [contentType]) || null,
        },
        take: itemsPerPage,
        skip: (currentPage - 1) * itemsPerPage,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    appliedFilters,
    itemsPerPage,
    currentPage,
    sitecoreContext.language,
    sitecoreContext.site?.name,
    contentType,
  ]);

  const mappedArticleData: ArticleFields[] = useMemo((): ArticleFields[] => {
    return (
      data?.content?.map((item) => ({
        articleImage: item.article_image ?? undefined,
        articlePublicationDate: {
          formattedDate: item.article_publication_date,
        },
        articleStrapline: {
          value: item.article_strapline ?? '',
        },
        articleTitle: {
          value: item.article_title ?? '',
        },
        articleType: item.article_type ?? '',
        url: item.article_url ?? '',
        id: item.id,
        category: (item.article_categories && item.article_categories[0]) || '',
        name: item.article_title ?? '',
        publicationDate: item.article_publication_date ?? '',
        duration: {
          value: item.article_duration ?? 0,
        },
      })) || []
    );
  }, [data]);

  return (
    <ArticleCardsComponent
      articleCards={mappedArticleData}
      totalItems={data?.total_item ?? 0}
      pageNumber={currentPage}
      pageSize={itemsPerPage}
      onPageNumberChange={handlePageNumberChange}
      onPageSizeChange={handlePageSizeChange}
      rendering={rendering}
      isLoading={isFetching}
    />
  );
};

export default ArticleCards;
